import { createApiClient, type ApiClient, getAccessToken } from '@acitra/api-client';
import type {
  Tenant,
  TenantWithRole,
  Address,
  InvoiceStatus,
  InvoiceListItem,
  InvoiceDetail,
  InvoiceEdits,
  InvoiceLineItem,
  SyncStatus,
  SyncStatusResponse,
  QBCompanyInfo,
  VendorListItem,
  AccountListItem,
  AccountClassification,
  ListAccountsParams,
  ValidateConnectionResponse,
  VendorSuggestion,
  GetVendorSuggestionsResponse,
  MatchVendorRequest,
  MatchVendorResponse,
  VendorMatchCriteria,
  EntityMatch,
  VendorMatchingRule,
  CurrencyListItem,
  MatchCurrencyRequest,
  MatchCurrencyResponse,
  CreateVendorRequest,
  PartyInfo,
  VendorInfo,
} from '@acitra/shared';
import { loadConfig } from '../config';

// Re-export types for convenience
export type {
  Address,
  InvoiceStatus,
  InvoiceListItem,
  InvoiceDetail,
  SyncStatus,
  SyncStatusResponse,
  QBCompanyInfo,
  VendorListItem,
  AccountListItem,
  AccountClassification,
  TenantWithRole,
  ValidateConnectionResponse,
};

let apiClient: ApiClient | null = null;
let cachedToken: string | null = null;

async function getApiClient(): Promise<ApiClient> {
  // Refresh the token before each request batch
  cachedToken = await getAccessToken();

  if (!apiClient) {
    const config = await loadConfig();
    apiClient = createApiClient({
      baseUrl: config.apiUrl,
      getAccessToken: () => cachedToken,
      onUnauthorized: () => {
        // Redirect to login on 401
        window.location.href = '/login';
      },
    });
  }
  return apiClient;
}

/**
 * Get the QuickBooks OAuth URL to initiate the connection flow
 */
export async function getQuickBooksAuthUrl(): Promise<string> {
  const client = await getApiClient();
  const response = await client.get<{ authUrl: string }>('/api/qb/auth-url');
  return response.data.authUrl;
}

/**
 * List all tenants the current user belongs to
 */
export async function listTenants(): Promise<TenantWithRole[]> {
  const client = await getApiClient();
  const response = await client.get<{ tenants: TenantWithRole[] }>('/api/tenants');
  return response.data.tenants;
}

/**
 * Create a new tenant
 */
export async function createTenant(name: string): Promise<Tenant> {
  const client = await getApiClient();
  const response = await client.post<{ tenant: Tenant }>('/api/tenants', { name });
  return response.data.tenant;
}

// ========================================
// Invoice API
// ========================================

/**
 * List all invoices for the current tenant
 */
export async function listInvoices(tenantId: string): Promise<InvoiceListItem[]> {
  const client = await getApiClient();
  const response = await client.get<{ invoices: InvoiceListItem[] }>('/api/invoices', {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data.invoices;
}

/**
 * Get a presigned URL for uploading an invoice document
 */
export async function getInvoiceUploadUrl(
  tenantId: string,
  filename: string,
  contentType: string
): Promise<{ uploadUrl: string; invoiceId: string; s3Key: string }> {
  const client = await getApiClient();
  const response = await client.post<{ uploadUrl: string; invoiceId: string; s3Key: string }>(
    '/api/invoices/upload-url',
    { filename, contentType },
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Upload an invoice document to S3 using a presigned URL
 */
export async function uploadInvoiceToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }
}

/**
 * Get a single invoice by ID
 */
export async function getInvoice(tenantId: string, invoiceId: string): Promise<InvoiceDetail> {
  const client = await getApiClient();
  const response = await client.get<{ invoice: InvoiceDetail }>(`/api/invoices/${invoiceId}`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data.invoice;
}

/**
 * Get a presigned URL for viewing/downloading an invoice document
 */
export async function getInvoiceDocumentUrl(
  tenantId: string,
  invoiceId: string
): Promise<{ documentUrl: string; expiresIn: number }> {
  const client = await getApiClient();
  const response = await client.get<{ documentUrl: string; expiresIn: number }>(
    `/api/invoices/${invoiceId}/document-url`,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Delete an invoice (removes from both DynamoDB and S3)
 */
export async function deleteInvoice(tenantId: string, invoiceId: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/invoices/${invoiceId}`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
}

/**
 * Update editable fields on an invoice.
 * Edits are stored separately from extracted data to preserve audit trail.
 */
export async function updateInvoice(
  tenantId: string,
  invoiceId: string,
  edits: {
    invoiceNumber?: string | null;
    purchaseOrderNumber?: string | null;
    invoiceDate?: string | null;
    dueDate?: string | null;
    notes?: string | null;
    lineItems?: InvoiceLineItem[] | null;
    subtotal?: number | null;
    total?: number | null;
    billedTo?: PartyInfo | null;
    shippedTo?: PartyInfo | null;
    vendor?: VendorInfo | null;
  },
  resetEdits?: boolean
): Promise<{ success: boolean; edits: InvoiceEdits }> {
  const client = await getApiClient();
  const response = await client.put<{ success: boolean; edits: InvoiceEdits }>(
    `/api/invoices/${invoiceId}`,
    { edits, resetEdits },
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Retry invoice extraction for a failed invoice
 */
export async function retryExtraction(
  tenantId: string,
  invoiceId: string
): Promise<{ message: string; invoiceId: string }> {
  const client = await getApiClient();
  const response = await client.post<{ message: string; invoiceId: string }>(
    `/api/invoices/${invoiceId}/retry-extraction`,
    {},
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Re-run matching rules for an invoice
 * Clears existing matches and re-applies all matching rules (vendor, GL accounts, etc.)
 */
export async function retryMatching(
  tenantId: string,
  invoiceId: string
): Promise<{ message: string; invoiceId: string }> {
  const client = await getApiClient();
  const response = await client.post<{ message: string; invoiceId: string }>(
    `/api/invoices/${invoiceId}/retry-matching`,
    {},
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

// ========================================
// QuickBooks Sync API
// ========================================

/**
 * Get the sync status for the current tenant
 */
export async function getSyncStatus(tenantId: string): Promise<SyncStatusResponse> {
  const client = await getApiClient();
  const response = await client.get<SyncStatusResponse>('/api/qb/sync/status', {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data;
}

/**
 * Trigger a manual sync from QuickBooks
 */
export async function triggerSync(
  tenantId: string,
  syncType: 'full' | 'vendors' | 'accounts' | 'company' = 'full'
): Promise<{ message: string; syncType: string; tenantId: string }> {
  const client = await getApiClient();
  const response = await client.post<{ message: string; syncType: string; tenantId: string }>(
    '/api/qb/sync',
    { syncType },
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Disconnect QuickBooks from the current tenant
 */
export async function disconnectQuickBooks(
  tenantId: string
): Promise<{ message: string; tenantId: string; disconnectedAt: string }> {
  const client = await getApiClient();
  const response = await client.post<{
    message: string;
    tenantId: string;
    disconnectedAt: string;
  }>(
    '/api/qb/disconnect',
    {},
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

// ========================================
// Vendors API
// ========================================

/**
 * List all vendors for the current tenant
 */
export async function listVendors(
  tenantId: string,
  options?: { active?: boolean }
): Promise<VendorListItem[]> {
  const client = await getApiClient();
  const params = new URLSearchParams();
  if (options?.active !== undefined) {
    params.set('active', String(options.active));
  }
  const queryString = params.toString();
  const url = queryString ? `/api/vendors?${queryString}` : '/api/vendors';

  const response = await client.get<{ vendors: VendorListItem[] }>(url, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data.vendors;
}

// ========================================
// Accounts API (GL Accounts / Chart of Accounts)
// ========================================

/**
 * List all GL accounts for the current tenant
 */
export async function listAccounts(
  tenantId: string,
  options?: ListAccountsParams
): Promise<AccountListItem[]> {
  const client = await getApiClient();
  const params = new URLSearchParams();
  if (options?.active !== undefined) {
    params.set('active', String(options.active));
  }
  if (options?.type) {
    params.set('type', options.type);
  }
  if (options?.classification) {
    params.set('classification', options.classification);
  }
  const queryString = params.toString();
  const url = queryString ? `/api/accounts?${queryString}` : '/api/accounts';

  const response = await client.get<{ accounts: AccountListItem[] }>(url, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data.accounts;
}

// ========================================
// Tenant Membership API
// ========================================

/**
 * Validate the QuickBooks connection for a tenant.
 * This checks if the connection is still active and refreshes tokens if needed.
 */
export async function validateQBConnection(tenantId: string): Promise<ValidateConnectionResponse> {
  const client = await getApiClient();
  const response = await client.get<ValidateConnectionResponse>('/api/qb/validate-connection', {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data;
}

/**
 * Leave a tenant (remove current user from the tenant).
 * Cannot leave if user is the only owner.
 */
export async function leaveTenant(tenantId: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/tenants/${tenantId}/membership`);
}

/**
 * Update the lastUsedAt timestamp for a tenant.
 * Called when selecting/switching to a tenant.
 */
export async function updateLastUsedTenant(tenantId: string): Promise<void> {
  const client = await getApiClient();
  await client.put(
    '/api/tenants/last-used',
    {},
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
}

// ========================================
// Team Management API
// ========================================

import type {
  TenantUser,
  TenantInvitation,
  TenantUserRole,
  InvitationStatus,
  TenantSettings,
} from '@acitra/shared';

export type { TenantUser, TenantInvitation, TenantUserRole, InvitationStatus, TenantSettings };

export interface TenantUserWithDetails {
  cognitoSub: string;
  email: string;
  name?: string;
  role: TenantUserRole;
  joinedAt: string;
  invitedBy?: string;
}

export interface ListUsersResponse {
  users: TenantUserWithDetails[];
  invitations: TenantInvitation[];
}

export interface InviteUserResponse {
  invitation: TenantInvitation;
}

export interface GetInvitationResponse {
  email: string;
  tenantName: string;
  inviterEmail?: string;
  role: TenantUserRole;
  expiresAt: string;
  status: InvitationStatus;
}

export interface AcceptInvitationResponse {
  tenantId: string;
  role: TenantUserRole;
}

/**
 * List all users and pending invitations for a tenant
 */
export async function listTenantUsers(tenantId: string): Promise<ListUsersResponse> {
  const client = await getApiClient();
  const response = await client.get<ListUsersResponse>(`/api/tenants/${tenantId}/users`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data;
}

/**
 * Invite a new user to the tenant
 */
export async function inviteUser(
  tenantId: string,
  email: string,
  role: TenantUserRole
): Promise<TenantInvitation> {
  const client = await getApiClient();
  const response = await client.post<InviteUserResponse>(
    `/api/tenants/${tenantId}/users/invite`,
    { email, role },
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data.invitation;
}

/**
 * Update a user's role in the tenant
 */
export async function updateUserRole(
  tenantId: string,
  cognitoSub: string,
  role: TenantUserRole
): Promise<void> {
  const client = await getApiClient();
  await client.put(
    `/api/tenants/${tenantId}/users/${cognitoSub}/role`,
    { role },
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
}

/**
 * Remove a user from the tenant
 */
export async function removeUser(tenantId: string, cognitoSub: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/tenants/${tenantId}/users/${cognitoSub}`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
}

/**
 * Get invitation details by token (public endpoint)
 */
export async function getInvitation(token: string): Promise<GetInvitationResponse> {
  const client = await getApiClient();
  const response = await client.get<GetInvitationResponse>(`/api/invitations/${token}`);
  return response.data;
}

/**
 * Accept an invitation
 */
export async function acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
  const client = await getApiClient();
  const response = await client.post<AcceptInvitationResponse>(
    `/api/invitations/${token}/accept`,
    {}
  );
  return response.data;
}

/**
 * Cancel a pending invitation
 */
export async function cancelInvitation(tenantId: string, invitationId: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/tenants/${tenantId}/invitations/${invitationId}`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
}

// ========================================
// Tenant Settings API
// ========================================

export interface GetSettingsResponse {
  settings: TenantSettings;
}

export interface UpdateSettingsRequest {
  lineItemNumericPrecision?: number;
}

export interface UpdateSettingsResponse {
  settings: TenantSettings;
}

/**
 * Get tenant settings
 * Any tenant member can read settings
 */
export async function getTenantSettings(tenantId: string): Promise<TenantSettings> {
  const client = await getApiClient();
  const response = await client.get<GetSettingsResponse>(`/api/tenants/${tenantId}/settings`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data.settings;
}

/**
 * Update tenant settings
 * Only owners and admins can update settings
 */
export async function updateTenantSettings(
  tenantId: string,
  settings: UpdateSettingsRequest
): Promise<TenantSettings> {
  const client = await getApiClient();
  const response = await client.put<UpdateSettingsResponse>(
    `/api/tenants/${tenantId}/settings`,
    settings,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data.settings;
}

// ========================================
// Invitation Signup API (Public)
// ========================================

export interface SignupWithInvitationRequest {
  token: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface SignupWithInvitationResponse {
  success: boolean;
  email: string;
  tenantId: string;
  role: TenantUserRole;
}

/**
 * Sign up a new user via invitation token.
 * This is a PUBLIC endpoint that creates the Cognito user and adds them to the tenant.
 * The invitation serves as email verification (user clicked the link in their email).
 */
export async function signupWithInvitation(
  request: SignupWithInvitationRequest
): Promise<SignupWithInvitationResponse> {
  // Get API URL from config (we need to make a raw fetch since user isn't authenticated)
  const config = await loadConfig();
  const response = await fetch(`${config.apiUrl}/api/invitations/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Signup failed' }));
    throw new Error(errorData.message || `Signup failed with status ${response.status}`);
  }

  return response.json();
}

// ========================================
// Vendor Matching API
// ========================================

export type {
  VendorSuggestion,
  GetVendorSuggestionsResponse,
  MatchVendorRequest,
  MatchVendorResponse,
  VendorMatchCriteria,
  EntityMatch,
  VendorMatchingRule,
};

/**
 * Search vendors by name, company, or email
 */
export async function searchVendors(
  tenantId: string,
  query: string,
  limit = 10
): Promise<VendorListItem[]> {
  const client = await getApiClient();
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const response = await client.get<{ vendors: VendorListItem[] }>(
    `/api/vendors/search?${params}`,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data.vendors;
}

/**
 * Create a new vendor in QuickBooks
 */
export async function createVendor(
  tenantId: string,
  vendor: CreateVendorRequest
): Promise<VendorListItem> {
  const client = await getApiClient();
  const response = await client.post<{ vendor: VendorListItem }>('/api/vendors', vendor, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data.vendor;
}

/**
 * Get vendor suggestions for an invoice based on extracted data
 */
export async function getVendorSuggestions(
  tenantId: string,
  invoiceId: string
): Promise<GetVendorSuggestionsResponse> {
  const client = await getApiClient();
  const response = await client.get<GetVendorSuggestionsResponse>(
    `/api/invoices/${invoiceId}/vendor-suggestions`,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Match an invoice to a QuickBooks vendor
 */
export async function matchVendor(
  tenantId: string,
  invoiceId: string,
  request: MatchVendorRequest
): Promise<MatchVendorResponse> {
  const client = await getApiClient();
  const response = await client.post<MatchVendorResponse>(
    `/api/invoices/${invoiceId}/match-vendor`,
    request,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Remove vendor match from an invoice
 */
export async function unmatchVendor(tenantId: string, invoiceId: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/invoices/${invoiceId}/match-vendor`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
}

// ========================================
// Vendor Matching Rules API
// ========================================

import type { VendorMatchingRuleListItem } from '@acitra/shared';

export type { VendorMatchingRuleListItem };

/**
 * List all vendor matching rules for the current tenant
 */
export async function listVendorMatchingRules(
  tenantId: string
): Promise<VendorMatchingRuleListItem[]> {
  const client = await getApiClient();
  const response = await client.get<{ rules: VendorMatchingRuleListItem[] }>(
    '/api/vendor-matching-rules',
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data.rules;
}

/**
 * Delete a vendor matching rule
 */
export async function deleteVendorMatchingRule(tenantId: string, ruleId: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/vendor-matching-rules/${ruleId}`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
}

// ========================================
// Terms (Payment Terms) API
// ========================================

import type {
  TermListItem,
  CreateTermRequest,
  MatchTermRequest,
  MatchTermResponse,
} from '@acitra/shared';

export type { TermListItem };

/**
 * List all payment terms for the current tenant
 */
export async function listTerms(
  tenantId: string,
  options?: { active?: boolean }
): Promise<TermListItem[]> {
  const client = await getApiClient();
  const params = new URLSearchParams();
  if (options?.active !== undefined) {
    params.set('active', String(options.active));
  }

  const response = await client.get<{ terms: TermListItem[] }>(
    `/api/terms${params.toString() ? `?${params.toString()}` : ''}`,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data.terms;
}

/**
 * Create a new payment term in QuickBooks
 */
export async function createTerm(tenantId: string, term: CreateTermRequest): Promise<TermListItem> {
  const client = await getApiClient();
  const response = await client.post<{ term: TermListItem }>('/api/terms', term, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
  return response.data.term;
}

/**
 * Match an invoice to a QuickBooks payment term
 */
export async function matchTerm(
  tenantId: string,
  invoiceId: string,
  request: MatchTermRequest
): Promise<MatchTermResponse> {
  const client = await getApiClient();
  const response = await client.post<MatchTermResponse>(
    `/api/invoices/${invoiceId}/match-term`,
    request,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Remove payment term match from an invoice
 */
export async function unmatchTerm(tenantId: string, invoiceId: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/invoices/${invoiceId}/term-match`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
}

// ========================================
// Currency API
// ========================================

/**
 * List all company currencies for the current tenant
 */
export async function listCurrencies(
  tenantId: string,
  options?: { active?: boolean }
): Promise<CurrencyListItem[]> {
  const client = await getApiClient();
  const params = new URLSearchParams();
  if (options?.active !== undefined) {
    params.set('active', String(options.active));
  }

  const response = await client.get<{ currencies: CurrencyListItem[] }>(
    `/api/currencies${params.toString() ? `?${params.toString()}` : ''}`,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data.currencies;
}

/**
 * Match an invoice to a QuickBooks company currency
 */
export async function matchCurrency(
  tenantId: string,
  invoiceId: string,
  request: MatchCurrencyRequest
): Promise<MatchCurrencyResponse> {
  const client = await getApiClient();
  const response = await client.post<MatchCurrencyResponse>(
    `/api/invoices/${invoiceId}/match-currency`,
    request,
    {
      headers: {
        'X-Tenant-Id': tenantId,
      },
    }
  );
  return response.data;
}

/**
 * Remove currency match from an invoice
 */
export async function unmatchCurrency(tenantId: string, invoiceId: string): Promise<void> {
  const client = await getApiClient();
  await client.delete(`/api/invoices/${invoiceId}/currency-match`, {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  });
}
